import {
  getPrice,
  ImageAsset,
  sumAdditionalPrice,
  unwrapPrice,
} from "@greeter/core";
import { IonContent, IonIcon, IonModal } from "@ionic/react";
import { Card, QuantityControls } from "@greeter/matter-v2";

import css from "./AlmostThereModal.module.scss";
import { checkmark } from "ionicons/icons";
import { IconLayoutGrid, IconX } from "@tabler/icons-react";
import { useEffect, useRef } from "react";
import { throttle } from "lodash";
import { classNames } from "@greeter/util";
import { Thumb } from "./Thumb";
import { formatMoney } from "@greeter/formatters";
import { bundleSumExtraPrice, bundleSumTotalPrice } from "./util";
import { BundleForm } from "../../models";

export type AlmostThereModalProps = {
  minPrice: number;
  bundle?: BundleForm;

  isOpen?: boolean;

  quantity: number;

  onChangeQuantity?: (quantity: number) => void;
  onChangeTables?: () => void;
  onContinue?: () => void;
  onBack?: () => void;
  onAddMore?: () => void;
};

export function AlmostThereModal(props: AlmostThereModalProps) {
  const onBackThrottle = useRef(throttle(() => props.onBack?.(), 1_000));
  useEffect(() => {
    onBackThrottle.current = throttle(() => props.onBack?.(), 1_000);
  }, [props.onBack]);

  const actualPrice = bundleSumTotalPrice(props.bundle);

  const total = actualPrice * props.quantity;
  const missing = Math.max(props.minPrice - total, 0);
  const hasMissing = missing > 0;

  return (
    <IonModal
      isOpen={props.isOpen}
      onWillDismiss={onBackThrottle.current}
      keepContentsMounted
      style={{
        backgroundColor: "var(--gm-color-default-bg)",
      }}
    >
      <IonContent>
        <div className={css.AlmostThereModal}>
          <div className={css.Header}>
            <h1 style={{ margin: 0 }}>Du er der næsten</h1>
            <p>
              Du mangler lidt for at sikre dit bord. Vælg om du vil tilføje en
              bordpakke mere eller skifte til et andet bord.
            </p>
            <div onClick={onBackThrottle.current} className={css.ExitButton}>
              <IconX size={20} style={{ color: "white" }} />
            </div>
          </div>

          <div
            style={{
              padding: "1rem",
              display: "flex",
              flexDirection: "column",
              gap: 20,
            }}
          >
            <Card>
              <Card.Content className={css.Summary}>
                <div className={css.Title}>Oversigt</div>
                <div>Nuværende bordpakke</div>
                <div className={css.Right}>{formatMoney(total)} kr.</div>
                <div>Minimum for bord</div>
                <div className={css.Right}>
                  {formatMoney(props.minPrice)} kr.
                </div>
                <div className={css.Line} />
                <div
                  {...classNames(
                    css.MissingText,
                    !hasMissing && css.Green,
                    css.Left
                  )}
                >
                  Mangler
                </div>
                <div
                  {...classNames(
                    css.MissingText,
                    !hasMissing && css.Green,
                    css.Right
                  )}
                >
                  {formatMoney(Math.max(missing, 0))} kr.
                </div>
              </Card.Content>
            </Card>

            <div className={css.Buttons}>
              <Card
                act="button"
                onClick={props.onChangeTables}
                style={{ backgroundColor: "var(--gm-color-default-bg)" }}
              >
                <Card.Content className={css.ChangeTableButton}>
                  <IconLayoutGrid />
                  Skift bord
                </Card.Content>
              </Card>
              {/*<Card act="button" onClick={props.onAddMore}>
                <Card.Content className={css.AddMoreButton}>
                  <IconPlus />
                  Tilføj mere
                </Card.Content>
              </Card>*/}
            </div>

            {props.bundle && actualPrice && (
              <Card>
                <Card.Content style={{ textAlign: "left" }}>
                  <h1 style={{ fontWeight: "bold", fontSize: 16 }}>
                    Nuværende bordpakke
                  </h1>
                  <div className={css.BundlePreview}>
                    <Thumb
                      src={ImageAsset.findUriWithSizeOrDefault(
                        props.bundle.coverAsset,
                        "1x1-w150"
                      )}
                    />
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-around",
                      }}
                    >
                      <span style={{ fontSize: 18, textAlign: "left" }}>
                        {props.bundle.title}
                      </span>
                      <span
                        style={{
                          fontSize: 14,
                          textAlign: "left",
                          opacity: 0.7,
                        }}
                      >
                        Bordpakke
                      </span>
                      <span style={{ fontSize: 16, textAlign: "left" }}>
                        {formatMoney(actualPrice)}
                      </span>
                    </div>
                    <QuantityControls>
                      <QuantityControls.EditButton onClick={props.onBack} />
                      <QuantityControls.MinusButton
                        onClick={() =>
                          props.onChangeQuantity?.(props.quantity - 1)
                        }
                      />
                      <QuantityControls.Value
                        value={props.quantity}
                        onChange={(ev) => {
                          let r = parseInt(ev.target.value);
                          if (isNaN(r)) {
                            r = 1;
                          }
                          props.onChangeQuantity?.(r);
                        }}
                      />
                      <QuantityControls.PlusButton
                        onClick={() =>
                          props.onChangeQuantity?.(props.quantity + 1)
                        }
                      />
                    </QuantityControls>
                  </div>
                </Card.Content>
              </Card>
            )}
          </div>
        </div>

        <div
          style={{
            position: "fixed",
            bottom: "var(--facebook-padding, 0px)",
            left: 0,
            display: "flex",
            width: "100%",

            backgroundColor: "rgba(var(--gm-color-default-bg-rgb-value), 0.6)",
            backdropFilter: "blur(5px)",
          }}
        >
          <div
            style={{
              display: "flex",
              maxWidth: 600,
              width: "100%",
              margin: "0 auto",
              padding: "1rem",
              justifyContent: "left",
            }}
          >
            <Card
              act="button"
              variant="green"
              style={{ width: "100%" }}
              onClick={props.onContinue}
            >
              <Card.Content preset="flex-aligned-center">
                <IonIcon icon={checkmark} style={{ strokeWidth: 2 }} />
                Fortsæt bookingen
              </Card.Content>
            </Card>
          </div>
        </div>
      </IonContent>
    </IonModal>
  );
}
